import { db } from './firebase';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  runTransaction,
  collection,
  getDocs,
  query,
  where,
  arrayUnion,
} from 'firebase/firestore';
import { BADGE_DEFINITIONS } from '../utils/badgeDefinitions';

export type BadgeId = keyof typeof BADGE_DEFINITIONS;

async function getUserDocRefByAuthUid(uid: string) {
  // For awards, always write to canonical users/{uid} to align with rules
  return doc(db, 'users', uid);
}

// Award the first user who comments on a post (top-level comment) by claiming a field on the post
export async function checkAndAwardFirstCommenterOnPost(postId: string, uid: string): Promise<boolean> {
  const postRef = doc(db, 'posts', postId);
  let claimed = false;
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(postRef);
    if (!snap.exists()) return; // no post
    const data = (snap.data() as any) || {};
    if (data.firstCommenterUserId) {
      // already claimed
      return;
    }
    // claim as first commenter
    tx.update(postRef, { firstCommenterUserId: uid });
    claimed = true;
  });
  return claimed;
}

// Atomically set both firstCommenterUserId and firstCommentId if vacant, validating the comment
export async function setFirstCommentIfVacant(postId: string, commentId: string, uid: string): Promise<boolean> {
  const postRef = doc(db, 'posts', postId);
  const cRef = doc(db, 'posts', postId, 'comments', commentId);
  let claimed = false;
  await runTransaction(db, async (tx) => {
    const postSnap = await tx.get(postRef);
    if (!postSnap.exists()) return;
    const post = (postSnap.data() as any) || {};
    if (post.firstCommenterUserId || post.firstCommentId) return; // already claimed

    const cSnap = await tx.get(cRef);
    if (!cSnap.exists()) return;
    const c = (cSnap.data() as any) || {};
    // Validate comment is top-level and authored by uid
    if ((c.parentId ?? null) !== null) return;
    if (c.userId !== uid) return;
    // Exclude system/bot or pending moderation
    if (c.isSystem === true) return;
    if (c.isBot === true) return;
    if (c.status === 'pending') return;

    tx.update(postRef, { firstCommenterUserId: uid, firstCommentId: commentId });
    claimed = true;
  });
  return claimed;
}

async function ensureUserDoc(uid: string) {
  const ref = await getUserDocRefByAuthUid(uid);
  try {
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      // Only set uid; avoid createdAt here to satisfy client write rules across doc-id variants
      await setDoc(ref, { uid }, { merge: true });
    } else {
      // Ensure uid field is set for older profiles
      const data = snap.data() as any;
      if (!data || data.uid !== uid) {
        try { await setDoc(ref, { uid }, { merge: true }); } catch {}
      }
    }
  } catch (e: any) {
    // Silently ignore permission errors
    if (e?.code !== 'permission-denied') {
      console.warn('[badges] ensureUserDoc error:', e);
    }
  }
  return ref;
}

export async function awardBadge(uid: string, badgeId: BadgeId): Promise<boolean> {
  const userRef = await ensureUserDoc(uid);
  try {
    // Avoid duplicate awards by a quick read; non-transactional is fine for badges
    const snap = await getDoc(userRef);
    const badges: Array<{ id: string }> = Array.isArray((snap.data() as any)?.badges) ? (snap.data() as any).badges : [];
    if (badges.some((b) => b.id === badgeId)) {
      console.debug('[badges] already has badge, skipping', { uid, badgeId });
      console.log('[badges] award result', { uid, badgeId, awarded: false, path: (userRef as any)?.path });
      return false;
    }
  } catch {}

  try {
    await updateDoc(userRef, {
      badges: arrayUnion({ id: badgeId, awardedAt: new Date() })
    });
    console.log('[badges] award result', { uid, badgeId, awarded: true, path: (userRef as any)?.path });
    return true;
  } catch (e) {
    // If update fails (e.g., missing doc), fallback to set with merge
    try {
      await setDoc(userRef, {
        badges: [{ id: badgeId, awardedAt: new Date() }]
      }, { merge: true });
      console.log('[badges] award result', { uid, badgeId, awarded: true, path: (userRef as any)?.path, via: 'set-merge' });
      return true;
    } catch (e2) {
      console.warn('[badges] award failed', { uid, badgeId, error: String(e2) });
      return false;
    }
  }
}

export async function hasBadge(uid: string, badgeId: BadgeId): Promise<boolean> {
  const ref = await getUserDocRefByAuthUid(uid);
  const snap = await getDoc(ref);
  const data = (snap.data() as any) || {};
  const badges: Array<{ id: string }> = Array.isArray(data.badges) ? data.badges : [];
  return badges.some((b) => b.id === badgeId);
}

export async function removeBadge(uid: string, badgeId: BadgeId): Promise<boolean> {
  const userRef = await ensureUserDoc(uid);
  try {
    const snap = await getDoc(userRef);
    const badges: Array<{ id: string; awardedAt: any }> = Array.isArray((snap.data() as any)?.badges) ? (snap.data() as any).badges : [];
    const filteredBadges = badges.filter((b) => b.id !== badgeId);
    
    if (filteredBadges.length === badges.length) {
      // Badge not found, nothing to remove
      console.debug('[badges] badge not found, nothing to remove', { uid, badgeId });
      return false;
    }
    
    await updateDoc(userRef, { badges: filteredBadges });
    console.log('[badges] remove result', { uid, badgeId, removed: true, path: (userRef as any)?.path });
    return true;
  } catch (e) {
    console.warn('[badges] remove failed', { uid, badgeId, error: String(e) });
    return false;
  }
}

export async function checkAndAwardFirstUpload(uid: string): Promise<boolean> {
  // Count posts by this user; if 1, award
  const q = query(collection(db, 'posts'), where('userId', '==', uid));
  const snap = await getDocs(q);
  const eligible = snap.size === 1;
  console.debug('[badges] first_upload check', { uid, postsByUser: snap.size, eligible });
  if (eligible) {
    return awardBadge(uid, 'first_upload');
  }
  return false;
}

export async function checkAndAwardCommunityPillar(uid: string): Promise<boolean> {
  // Count total comments by user across all posts
  const commentsQ = query(collection(db, 'posts'));
  const postsSnap = await getDocs(commentsQ);
  
  let totalComments = 0;
  for (const postDoc of postsSnap.docs) {
    const commentsSnap = await getDocs(
      query(collection(db, 'posts', postDoc.id, 'comments'), where('userId', '==', uid))
    );
    totalComments += commentsSnap.size;
  }
  
  // Count total reposts by user
  const repostsSnap = await getDocs(
    query(collection(db, 'posts'))
  );
  let totalReposts = 0;
  for (const postDoc of repostsSnap.docs) {
    const repostSnap = await getDoc(doc(db, 'posts', postDoc.id, 'reposts', uid));
    if (repostSnap.exists()) {
      totalReposts += 1;
    }
  }
  
  const totalEngagement = totalComments + totalReposts;
  const eligible = totalEngagement >= 100;
  console.debug('[badges] community_pillar check', { uid, totalComments, totalReposts, totalEngagement, eligible });
  
  if (eligible) {
    return awardBadge(uid, 'community_pillar');
  }
  return false;
}

export async function checkAndAwardDailyVisitor(uid: string): Promise<boolean> {
  const streakRef = doc(db, 'users', uid, 'meta', 'login');
  // Ensure user doc exists
  await ensureUserDoc(uid);
  
  // Check if user already has the badge before processing
  const hadBadge = await hasBadge(uid, 'daily_visitor');
  
  // Store transaction result to detect streak break
  let txResult: { streakBroken: boolean; currentStreak: number };
  try {
    txResult = await runTransaction(db, async (tx) => {
      const snap = await tx.get(streakRef);
      const today = new Date();
      const ymd = today.toISOString().slice(0, 10);
      const data = (snap.exists() ? (snap.data() as any) : {}) || {};
      const last = typeof data.last === 'string' ? data.last : null;
      let streak = Number.isFinite(data.streak) ? Number(data.streak) : 0;
      let streakBroken = false;
      
      if (last === ymd) {
        // already recorded today - no changes
        return { streakBroken: false, currentStreak: streak };
      } else {
        // compute if consecutive
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        const ymdYesterday = yesterday.toISOString().slice(0, 10);
        if (last === ymdYesterday) {
          streak += 1; // Continue streak
        } else {
          // Streak broken - reset to day 1
          if (streak > 0) {
            streakBroken = true;
          }
          streak = 1;
        }
        tx.set(streakRef, { last: ymd, streak }, { merge: true });
        return { streakBroken, currentStreak: streak };
      }
    });
  } catch (e: any) {
    // Silently ignore permission errors
    if (e?.code !== 'permission-denied') {
      console.warn('[badges] Transaction error:', e);
    }
    // Return default result to prevent further errors
    return false;
  }
  
  // If streak was broken and user had the badge, remove it
  if (txResult.streakBroken && hadBadge) {
    await removeBadge(uid, 'daily_visitor');
    console.log('[badges] daily_visitor removed due to streak break', { uid });
  }
  
  // Check if eligible to award badge
  const eligible = txResult.currentStreak >= 7;
  console.debug('[badges] daily_visitor check', { 
    uid, 
    streak: txResult.currentStreak, 
    eligible, 
    hadBadge, 
    streakBroken: txResult.streakBroken 
  });
  
  if (eligible) {
    return awardBadge(uid, 'daily_visitor');
  }
  return false;
}

export async function checkAndAwardLikedByMany(postId: string): Promise<boolean> {
  // Award post owner when a post reaches 20 likes
  const postRef = doc(db, 'posts', postId);
  const postSnap = await getDoc(postRef);
  if (!postSnap.exists()) return false;
  const ownerId = (postSnap.data() as any)?.userId as string | undefined;
  if (!ownerId) return false;
  const likesSnap = await getDocs(collection(db, 'posts', postId, 'likes'));
  const eligible = likesSnap.size >= 20;
  console.debug('[badges] liked_by_many check', { postId, ownerId, likes: likesSnap.size, eligible });
  
  if (eligible) {
    return awardBadge(ownerId, 'liked_by_many');
  }
  return false;
}
